/**
 * CRM overview — funnel shape and what needs attention.
 *
 * Deliberately not a wall of charts. The two things a sales manager opens
 * this for are whether the weighted forecast covers the number, and which
 * leads nobody has picked up.
 *
 * The pipeline and the forecast come from GET /v1/crm/opportunities/summary,
 * summed by the API per currency over every open deal: a headline in the
 * workspace currency and the other currencies beneath it, never added in.
 * (They used to be summed here over the first page of rows and labelled "$"
 * whatever the workspace, CUR-2.) Each row shows its own currency.
 */
import { Link } from "react-router-dom";
import {
  Card,
  DataTable,
  EmptyState,
  PageHeader,
  Skeleton,
  StatCard,
  StatusBadge,
} from "@/components/ui";
import { MoneyStatCard } from "@/components/Money";
import { QueryError } from "@/components/QueryError";
import { useApiList, useApiQuery } from "@/lib/api/hooks";
import { formatDate, formatMoney } from "@/lib/money";
import {
  personName,
  type ActivityRow,
  type LeadRow,
  type OpportunityRow,
  type OpportunitySummary,
} from "./types";

export function CrmDashboardPage() {
  const leads = useApiList<LeadRow>(["crm", "leads"], "/crm/leads");
  const opportunities = useApiList<OpportunityRow>(
    ["crm", "opportunities"],
    "/crm/opportunities",
  );
  const activities = useApiList<ActivityRow>(
    ["crm", "activities"],
    "/crm/activities",
  );
  const summary = useApiQuery<OpportunitySummary>(
    ["crm", "opportunities", "summary"],
    "/crm/opportunities/summary",
  );

  // Pending, not only fetching: a retry waiting for the window to regain
  // focus is "paused", and is not a failure.
  if (leads.isPending || opportunities.isPending || summary.isPending)
    return <Skeleton rows={10} />;

  const failed = [leads, opportunities, summary].find((q) => q.isError);
  if (failed || !summary.data)
    return (
      <QueryError
        error={failed?.error}
        retrying={[leads, opportunities, summary].some((q) => q.isFetching)}
        onRetry={() => {
          void leads.refetch();
          void opportunities.refetch();
          void summary.refetch();
        }}
      />
    );

  const leadRows = leads.data?.items ?? [];
  const oppRows = opportunities.data?.items ?? [];
  const unassigned = leadRows.filter((l) => !l.owner_user_id);

  const empty = leadRows.length === 0 && oppRows.length === 0;

  return (
    <div>
      <PageHeader
        title="CRM"
        subtitle="Funnel shape, weighted forecast, and what is going unattended."
      />

      <div className="atlas-grid atlas-grid--4 mb-4">
        <StatCard label="Leads" value={leads.data?.total ?? 0} />
        <StatCard
          label="Opportunities"
          value={opportunities.data?.total ?? 0}
        />
        <MoneyStatCard label="Pipeline" figure={summary.data.pipeline} />
        <MoneyStatCard
          label="Weighted forecast"
          figure={summary.data.weighted}
          meta="Value × probability"
        />
      </div>

      {empty ? (
        <EmptyState
          title="Nothing in the funnel yet"
          message="Add a lead to see qualification flow through to a forecastable opportunity."
          action={
            <Link to="/app/crm/leads">
              <span className="atlas-btn atlas-btn--primary">Go to leads</span>
            </Link>
          }
        />
      ) : (
        <>
          {unassigned.length > 0 && (
            <Card
              title={`Unassigned leads (${unassigned.length})`}
              className="mb-4"
            >
              <p className="atlas-muted" style={{ fontSize: 13 }}>
                Nobody owns these, so nobody is following them up.
              </p>
              <DataTable<LeadRow>
                rows={unassigned.slice(0, 8)}
                columns={[
                  {
                    key: "name",
                    label: "Name",
                    render: (r) => personName(r.first_name, r.last_name),
                  },
                  {
                    key: "company_name",
                    label: "Company",
                    render: (r) => r.company_name ?? "—",
                  },
                  {
                    key: "source",
                    label: "Source",
                    render: (r) => r.source ?? "—",
                  },
                  {
                    key: "status",
                    label: "Status",
                    render: (r) => <StatusBadge status={r.status} />,
                  },
                ]}
              />
              <Link to="/app/crm/leads">All leads</Link>
            </Card>
          )}

          <Card title="Closing soonest">
            {oppRows.length === 0 ? (
              <EmptyState
                title="No opportunities"
                message="Qualify a lead to create one."
              />
            ) : (
              <DataTable<OpportunityRow>
                rows={[...oppRows]
                  .sort((a, b) =>
                    (a.expected_close_date ?? "9999").localeCompare(
                      b.expected_close_date ?? "9999",
                    ),
                  )
                  .slice(0, 8)}
                columns={[
                  { key: "name", label: "Opportunity" },
                  { key: "stage", label: "Stage" },
                  {
                    key: "expected_value",
                    label: "Value",
                    numeric: true,
                    render: (r) =>
                      r.expected_value
                        ? formatMoney(r.expected_value, r.currency_code)
                        : "—",
                  },
                  {
                    key: "expected_close_date",
                    label: "Expected close",
                    render: (r) =>
                      r.expected_close_date
                        ? formatDate(r.expected_close_date)
                        : "—",
                  },
                ]}
              />
            )}
          </Card>

          {(activities.data?.items.length ?? 0) > 0 && (
            <Card title="Recent activity" className="mt-4">
              <ul>
                {activities.data?.items.slice(0, 6).map((a) => (
                  <li key={a.id}>
                    <strong>{a.activity_type}</strong> on {a.entity_type} —{" "}
                    {a.note ?? "no note"}
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
